import React, { useEffect, useRef, useState } from 'react';
import { safeArray } from '../../../components/ErrorBoundary';
import TableEmptyRow from '../../../components/TableEmptyRow';
import StatusBadge from '../../../components/StatusBadge';
import { banksAPI } from '../../../services/bgFinanceService';
import { emptyBankForm } from './bgShared';
import BgExportButtons from './BgExportButtons';
import { downloadBgTableCsv, exportBgTablePdf } from './bgExportUtils';
import { notifyError, notifySuccess, notifyWarning } from '../../../utils/notify';
import { confirmDialog } from '../../../utils/confirmDialog';

const mapBank = (row) => ({
  id: row.id,
  bank_name: row.bank_name || '',
  branch_name: row.branch_name || '',
  ifsc_code: row.ifsc_code || '',
  contact_person: row.contact_person || '',
  contact_email: row.contact_email || '',
  contact_phone: row.contact_phone || '',
  address: row.address || '',
  is_active: row.is_active !== false,
});

const BankMaster = () => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyBankForm);
  const tableRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);

  const loadBanks = async () => {
    try {
      const resp = await banksAPI.getAll();
      setBanks(safeArray(resp?.data?.data).map(mapBank));
    } catch (err) {
      console.error('Load banks failed:', err);
      notifyError(err?.response?.data?.message || 'Unable to load banks.');
      setBanks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadBanks(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyBankForm());
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      bank_name: row.bank_name || '',
      branch_name: row.branch_name || '',
      ifsc_code: row.ifsc_code || '',
      contact_person: row.contact_person || '',
      contact_email: row.contact_email || '',
      contact_phone: row.contact_phone || '',
      address: row.address || '',
      is_active: row.is_active !== false,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.bank_name.trim()) {
      notifyWarning('Bank name is required.');
      return;
    }
    if (!(await confirmDialog(editing ? 'Save bank changes?' : 'Create this bank?', editing ? 'Update Bank' : 'New Bank'))) return;

    setSubmitting(true);
    try {
      const payload = {
        bank_name: form.bank_name.trim(),
        branch_name: form.branch_name.trim() || null,
        ifsc_code: form.ifsc_code.trim() || null,
        contact_person: form.contact_person.trim() || null,
        contact_email: form.contact_email.trim() || null,
        contact_phone: form.contact_phone.trim() || null,
        address: form.address.trim() || null,
        is_active: !!form.is_active,
      };
      if (editing) {
        const resp = await banksAPI.update(editing.id, payload);
        const mapped = mapBank(resp?.data);
        setBanks((prev) => prev.map((b) => (b.id === editing.id ? mapped : b)));
        notifySuccess('Bank updated.');
      } else {
        const resp = await banksAPI.create(payload);
        const mapped = mapBank(resp?.data);
        setBanks((prev) => [...prev, mapped].sort((a, b) => a.bank_name.localeCompare(b.bank_name)));
        notifySuccess('Bank created.');
      }
      setShowModal(false);
    } catch (err) {
      console.error('Save bank failed:', err);
      notifyError(err?.response?.data?.message || 'Unable to save bank.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (row) => {
    if (!(await confirmDialog(`Deactivate ${row.bank_name}?`, 'Deactivate Bank'))) return;
    try {
      await banksAPI.delete(row.id);
      setBanks((prev) => prev.map((b) => (b.id === row.id ? { ...b, is_active: false } : b)));
      notifySuccess('Bank deactivated.');
    } catch (err) {
      console.error('Delete bank failed:', err);
      notifyError(err?.response?.data?.message || 'Unable to deactivate bank.');
    }
  };

  if (loading) {
    return <p style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>Loading banks...</p>;
  }


  const handleExportPdf = () => exportBgTablePdf({ title: 'Bank Master', subtitle: `${banks.length} bank(s)`, element: tableRef.current });
  const handleExportCsv = () => downloadBgTableCsv({ element: tableRef.current, filename: 'bank-master.csv' });

  return (
    <div>
      <div className="section-header">
        <h2>Bank Master <span className="count-badge">{banks.length}</span></h2>
        <div style={{ display: 'flex', gap: 8 }}><BgExportButtons onDownloadPdf={handleExportPdf} onDownloadCsv={handleExportCsv} /><button type="button" className="btn-primary" onClick={openCreate}>Add Bank</button></div>
      </div>

      <div className="table-container" ref={tableRef}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Bank Name</th>
              <th>Branch</th>
              <th>IFSC</th>
              <th>Contact Person</th>
              <th>Contact Phone</th>
              <th>Active</th>
              <th className="bg-export-exclude" style={{ width: 140 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {banks.length === 0 ? (
              <TableEmptyRow colSpan={7} message="No banks found." />
            ) : (
              banks.map((row) => (
                <tr key={row.id}>
                  <td style={{ fontWeight: 500 }}>{row.bank_name}</td>
                  <td>{row.branch_name || '—'}</td>
                  <td>{row.ifsc_code || '—'}</td>
                  <td>{row.contact_person || '—'}</td>
                  <td>{row.contact_phone || '—'}</td>
                  <td><StatusBadge tone={row.is_active ? 'active' : 'closed'}>{row.is_active ? 'Yes' : 'No'}</StatusBadge></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button type="button" className="btn-secondary btn-sm" onClick={() => openEdit(row)}>Edit</button>
                    {' '}
                    {row.is_active && (
                      <button type="button" className="btn-secondary btn-sm" style={{ color: '#b91c1c' }} onClick={() => handleDelete(row)}>Deactivate</button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editing ? 'Edit Bank' : 'Add Bank'}</h2>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Bank Name <span className="req">*</span></label>
                  <input type="text" value={form.bank_name} onChange={(e) => setForm((p) => ({ ...p, bank_name: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Branch</label>
                  <input type="text" value={form.branch_name} onChange={(e) => setForm((p) => ({ ...p, branch_name: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>IFSC Code</label>
                  <input type="text" value={form.ifsc_code} onChange={(e) => setForm((p) => ({ ...p, ifsc_code: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Contact Person</label>
                  <input type="text" value={form.contact_person} onChange={(e) => setForm((p) => ({ ...p, contact_person: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Contact Email</label>
                  <input type="email" value={form.contact_email} onChange={(e) => setForm((p) => ({ ...p, contact_email: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Contact Phone</label>
                  <input type="text" value={form.contact_phone} onChange={(e) => setForm((p) => ({ ...p, contact_phone: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label>Address</label>
                <textarea rows={2} value={form.address} onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))} style={{ width: '100%' }} />
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} />
                  Active
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)} disabled={submitting}>Cancel</button>
                <button type="button" className="btn-primary" onClick={handleSave} disabled={submitting}>{submitting ? 'Saving...' : 'Save'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankMaster;
